import { useEffect, useState, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, Link } from 'react-router-dom';
import { Loader } from './components/loader';
import { useAuth } from './lib/auth-context';
import { analytics } from './lib/analytics';

const LandingPage      = lazy(() => import('./pages/landing'));
const PrivacyPolicyPage = lazy(() => import('./pages/privacy-policy'));
const TermsPage        = lazy(() => import('./pages/terms'));
const LoginPage        = lazy(() => import('./pages/login'));
const OnboardingPage   = lazy(() => import('./pages/onboarding'));
const DashboardPage    = lazy(() => import('./pages/dashboard'));
const ProgramsPage     = lazy(() => import('./pages/programs'));
const PatronsPage      = lazy(() => import('./pages/patrons'));
const PatronDetailPage = lazy(() => import('./pages/patron-detail'));
const RewardsPage      = lazy(() => import('./pages/rewards'));
const SettingsPage     = lazy(() => import('./pages/settings'));
const MenuPage         = lazy(() => import('./pages/menu'));
const OrdersPage       = lazy(() => import('./pages/orders'));
const IntegrationsPage = lazy(() => import('./pages/integrations'));
const StampLogPage     = lazy(() => import('./pages/stamp-log'));
const AdminPage        = lazy(() => import('./pages/admin'));
import { api } from './lib/api';
import { useBilling } from './lib/billing-context';
import {
  LayoutDashboard,
  Gift,
  Users,
  UtensilsCrossed,
  ShoppingBag,
  BadgeCheck,
  Plug,
  Settings,
  LogOut,
  Activity,
  ShieldCheck,
} from 'lucide-react';

const NAV = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/programs', label: 'Programs', icon: Gift },
  { to: '/patrons', label: 'Patrons', icon: Users },
  { to: '/menu', label: 'Menu', icon: UtensilsCrossed },
  { to: '/orders', label: 'Orders', icon: ShoppingBag },
  { to: '/rewards', label: 'Rewards', icon: BadgeCheck },
  { to: '/stamp-log', label: 'Stamp Log', icon: Activity },
  { to: '/integrations', label: 'Integrations', icon: Plug },
  { to: '/settings', label: 'Settings', icon: Settings },
];

function Sidebar() {
  const { currentVenue, logout, isLoggedIn } = useAuth();
  const location = useLocation();
  const [rewardsBadge, setRewardsBadge] = useState(0);

  useEffect(() => {
    if (!isLoggedIn) return;
    api.getDashboard().then(r => {
      if (r.success && r.data) setRewardsBadge(r.data.pendingRewards || 0);
    });
  }, [isLoggedIn, location.pathname]);

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <aside className="sidebar">
      <div className="sidebar-logo flex items-center gap-2.5">
        <img src="/logo.svg" alt="Reperk" className="w-7 h-7" />
        <span>Reperk HQ</span>
      </div>

      <nav className="sidebar-nav">
        {NAV.map(({ to, label, icon: Icon }) => (
          <Link
            key={to}
            to={to}
            className={`sidebar-link ${isActive(to) ? 'sidebar-link-active' : ''}`}
          >
            <Icon />
            {label}
            {to === '/rewards' && rewardsBadge > 0 && (
              <span className="ml-auto min-w-[18px] h-[18px] rounded-full bg-amber-500 text-stone-900 text-[10px] font-bold flex items-center justify-center px-1 leading-none">
                {rewardsBadge > 99 ? '99+' : rewardsBadge}
              </span>
            )}
          </Link>
        ))}
        {currentVenue?.email === 'rahul@lonegibbon.com' && (
          <Link to="/admin" className={`sidebar-link ${isActive('/admin') ? 'sidebar-link-active' : ''}`}>
            <ShieldCheck />
            Admin
          </Link>
        )}
      </nav>

      <div className="sidebar-footer">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-7 h-7 rounded-full bg-amber-500 flex items-center justify-center text-stone-900 text-xs font-bold shrink-0">
            {currentVenue?.name?.[0]?.toUpperCase() ?? '?'}
          </div>
          <span className="sidebar-venue-name">{currentVenue?.name}</span>
        </div>
        <button onClick={logout} className="sidebar-logout flex items-center gap-1">
          <LogOut className="w-3.5 h-3.5" />
          Logout
        </button>
      </div>
    </aside>
  );
}

function TrialBanner() {
  const { billing } = useBilling();
  if (!billing || billing.isDemo || billing.status === 'ACTIVE' || billing.isBlocked) return null;
  if (billing.status !== 'TRIAL') return null;

  const days = billing.daysRemaining;
  if (days === null || days > 14) return null;

  const isUrgent = days <= 3;
  return (
    <div className={`px-4 py-2 text-center text-sm font-medium ${isUrgent ? 'bg-red-500 text-white' : 'bg-amber-400 text-stone-900'}`}>
      {days > 0
        ? `Your free trial ends in ${days} day${days === 1 ? '' : 's'}. To keep using Reperk, WhatsApp Rahul at +91 74115 15511 or email info@lonegibbon.com.`
        : 'Your trial has ended — contact us to continue: rahul@lonegibbon.com'}
    </div>
  );
}

function PaywallOverlay() {
  const { billing } = useBilling();
  const location = useLocation();
  if (!billing || !billing.isBlocked) return null;
  if (location.pathname === '/settings') return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/80 backdrop-blur-sm">
      <div className="bg-white rounded-2xl p-8 max-w-md mx-4 text-center shadow-2xl">
        <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
          <svg className="w-7 h-7 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M12 3a9 9 0 100 18A9 9 0 0012 3z" />
          </svg>
        </div>
        <h2 className="text-xl font-bold text-stone-900 mb-2">Trial Ended</h2>
        <p className="text-stone-600 mb-6 text-sm leading-relaxed">
          Your 30-day free trial has ended. To continue using Reperk and keep your patrons and loyalty data, subscribe for ₹499/month.
        </p>
        <div className="space-y-3">
          <a
            href="https://wa.me/917411515511?text=Hi%20Rahul%2C%20I%20want%20to%20subscribe%20to%20Reperk%20%E2%82%B9499%2Fmonth"
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full py-3 px-4 bg-green-600 text-white font-semibold rounded-xl hover:bg-green-700 transition-colors"
          >
            WhatsApp to Pay — ₹499/month
          </a>
          <a
            href="mailto:info@lonegibbon.com?subject=Reperk subscription&body=Hi Rahul, I'd like to subscribe to Reperk."
            className="block w-full py-3 px-4 bg-stone-100 text-stone-700 font-semibold rounded-xl hover:bg-stone-200 transition-colors"
          >
            Email: info@lonegibbon.com
          </a>
        </div>
        <p className="text-xs text-stone-400 mt-4">Your data is safe. It will be restored as soon as payment is confirmed.</p>
      </div>
    </div>
  );
}

function AppShell({ children }: { children: React.ReactNode }) {
  const { isLoggedIn } = useAuth();
  const location = useLocation();

  // Track every page view
  useEffect(() => {
    analytics.page(location.pathname);
  }, [location.pathname]);

  const noShell = !isLoggedIn || location.pathname === '/onboarding' || location.pathname === '/' || location.pathname === '/privacy-policy' || location.pathname === '/terms';
  if (noShell) return <>{children}</>;
  return (
    <div className="app-shell">
      <Sidebar />
      <main className="main-area">
        <TrialBanner />
        <div className="main-content">{children}</div>
      </main>
      <PaywallOverlay />
    </div>
  );
}

function LandingOrDashboard() {
  const { isLoggedIn } = useAuth();
  return isLoggedIn ? <Navigate to="/dashboard" /> : <LandingPage />;
}

export default function App() {
  const { isLoading } = useAuth();

  if (isLoading) {
    return <Loader fullScreen />;
  }

  return (
    <Router>
      <AppShell>
        <Suspense fallback={<Loader fullScreen />}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<Navigate to="/onboarding" />} />
          <Route path="/onboarding" element={<OnboardingPage />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/programs" element={<ProgramsPage />} />
          <Route path="/patrons" element={<PatronsPage />} />
          <Route path="/patrons/:id" element={<PatronDetailPage />} />
          <Route path="/menu" element={<MenuPage />} />
          <Route path="/orders" element={<OrdersPage />} />
          <Route path="/rewards" element={<RewardsPage />} />
          <Route path="/integrations" element={<IntegrationsPage />} />
          <Route path="/stamp-log" element={<StampLogPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/" element={<LandingOrDashboard />} />
        </Routes>
        </Suspense>
      </AppShell>
    </Router>
  );
}
